import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, useAdminApi } from "@/lib/api";

const HIDE = ["id", "notes", "status", "owner", "photo_path", "consent"];
const label = (k) => k.replace(/_/g, " ");

const PrivatePhoto = ({ path }) => {
  const req = useAdminApi();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    req("get", `/admin/files/${path}`, undefined, { responseType: "blob" }).then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path, req]);
  return url ? <img src={url} alt="Submitted player" data-testid="enquiry-photo" className="h-48 w-40 rounded-md object-cover" /> : <div className="h-48 w-40 animate-pulse rounded-md bg-white/5" />;
};

export default function EnquiryDetail({ kind, item, statuses, onClose, onSaved }) {
  const req = useAdminApi();
  const [status, setStatus] = useState(item.status);
  const [owner, setOwner] = useState(item.owner || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const doc = await req("patch", `/admin/enquiries/${kind}/${item.id}`, { status, owner, note: note || undefined });
      onSaved(doc);
      setNote("");
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const publish = async () => {
    try {
      await req("post", `/admin/enquiries/player/${item.id}/publish`);
      toast.success("Draft player profile created. Publish it from Player profiles.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="enquiry-detail" className="w-full overflow-y-auto border-white/10 bg-[#0A192F] text-white sm:max-w-xl">
        <SheetHeader><SheetTitle className="font-display text-3xl uppercase text-white">{item.full_name || item.brand_name || item.name}</SheetTitle></SheetHeader>
        {item.photo_path && <div className="mt-4"><PrivatePhoto path={item.photo_path} /></div>}
        <dl className="mt-6 divide-y divide-white/5 text-sm">
          {Object.entries(item).filter(([k]) => !HIDE.includes(k)).map(([k, v]) => (
            <div key={k} className="grid grid-cols-[140px_1fr] gap-3 py-2.5">
              <dt className="text-xs uppercase tracking-wider text-slate-500">{label(k)}</dt>
              <dd className="whitespace-pre-line break-words">{k === "highlight_url" && v ? <a href={v} target="_blank" rel="noreferrer" className="text-[#00E5FF] underline">{v}</a> : String(v || "—")}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-6 space-y-4 rounded-md border border-white/10 bg-[#112240] p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wider text-slate-400">Status</p>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="detail-status-select" className="rounded-sm border-white/15 bg-[#0A192F]"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/15 bg-[#112240] text-white">{statuses.map((s) => <SelectItem key={s} value={s} data-testid={`detail-status-${s}`}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wider text-slate-400">Follow-up owner</p>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="detail-owner-input" className="rounded-sm border-white/15 bg-[#0A192F]" placeholder="Team member name" />
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-slate-400">Add internal note</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-note-input" className="rounded-sm border-white/15 bg-[#0A192F]" />
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="button" disabled={busy} onClick={save} data-testid="detail-save-btn" className="rounded-full bg-[#00E5FF] px-5 py-2 text-xs font-bold uppercase tracking-wider text-[#0A192F] disabled:opacity-60">Save changes</button>
            {kind === "player" && (
              <>
                <button type="button" onClick={() => setStatus("approved")} data-testid="detail-approve-btn" className="rounded-full border border-[#00E5FF]/50 px-5 py-2 text-xs font-bold uppercase tracking-wider text-[#00E5FF]">Approve</button>
                <button type="button" onClick={() => setStatus("rejected")} data-testid="detail-reject-btn" className="rounded-full border border-red-400/50 px-5 py-2 text-xs font-bold uppercase tracking-wider text-red-300">Reject</button>
                {item.status === "approved" && <button type="button" onClick={publish} data-testid="detail-create-profile-btn" className="rounded-full border border-white/20 px-5 py-2 text-xs font-bold uppercase tracking-wider">Create player profile</button>}
              </>
            )}
          </div>
          {kind === "player" && <p className="text-xs text-slate-500">Approve / Reject sets the status; click Save to apply.</p>}
        </div>
        <div className="mt-6">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Notes</h3>
          <ul data-testid="detail-notes" className="space-y-2">
            {(item.notes || []).map((n, i) => <li key={i} className="rounded-sm bg-white/5 p-3 text-sm"><p>{n.text}</p><p className="mt-1 text-xs text-slate-500">{n.by} · {new Date(n.at).toLocaleString("en-IN")}</p></li>)}
            {!item.notes?.length && <li className="text-sm text-slate-500">No notes yet.</li>}
          </ul>
        </div>
      </SheetContent>
    </Sheet>
  );
}
